import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useAuth } from '../hooks/useAuth';
import type { ReactNode } from 'react';

const featureKeys = ['combat', 'movement', 'esp', 'anti', 'hud', 'license'] as const;

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const item = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const } },
};

export default function Landing() {
  const { t } = useTranslation();
  const { user, license } = useAuth();

  const ctaLabel = user
    ? license
      ? t('landing.ctaDashboard')
      : t('landing.ctaPurchase')
    : t('landing.cta');
  const ctaLink = user ? (license ? '/dashboard' : '/pricing') : '/register';

  return (
    <>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[120px]" />
          <motion.div
            className="absolute top-1/4 left-1/4 w-32 h-32 border border-primary/10 rounded-full"
            animate={{ y: [0, -20, 0], opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute top-1/3 right-1/4 w-24 h-24 border border-primary/10 rounded-full"
            animate={{ y: [0, 16, 0], opacity: [0.2, 0.5, 0.2] }}
            transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
          />
        </div>

        <div className="max-w-6xl mx-auto px-6 py-24 md:py-36 relative">
          <motion.div
            className="max-w-3xl"
            initial="hidden"
            animate="show"
            variants={container}
          >
            <motion.div variants={item}>
              <span className="inline-block text-xs text-primary uppercase tracking-widest mb-4 border border-primary/30 rounded-full px-3 py-1">
                {t('landing.badge')}
              </span>
            </motion.div>
            <motion.h1
              variants={item}
              className="font-display text-5xl md:text-7xl font-bold leading-[1.05] tracking-tight mb-6 text-balance"
            >
              {t('landing.heroTitle')}
            </motion.h1>
            <motion.p
              variants={item}
              className="text-lg md:text-xl text-muted mb-10 max-w-2xl leading-relaxed"
            >
              {t('landing.heroDesc')}
            </motion.p>
            <motion.div variants={item} className="flex flex-wrap gap-4">
              <CTAButton to={ctaLink}>{ctaLabel}</CTAButton>
              <Link
                to="/faq"
                className="text-base px-6 py-3 rounded border border-border hover:bg-surface-hover text-muted hover:text-foreground font-medium transition-colors"
              >
                {t('landing.learnMore')}
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="border-t border-border">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <motion.div
            className="grid md:grid-cols-3 gap-px bg-border rounded-lg overflow-hidden"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-80px' }}
            variants={container}
          >
            {featureKeys.map((key) => (
              <motion.div
                key={key}
                variants={item}
                className="bg-surface p-8 group hover:bg-surface-hover transition-colors"
              >
                <h3 className="font-display text-lg font-semibold mb-2 group-hover:text-primary transition-colors">
                  {t(`landing.features.${key}.title`)}
                </h3>
                <p className="text-sm text-muted leading-relaxed">
                  {t(`landing.features.${key}.desc`)}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="border-t border-border relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[500px] h-[300px] bg-primary/5 rounded-full blur-[100px]" />
        </div>
        <motion.div
          className="max-w-4xl mx-auto px-6 py-24 text-center relative"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <h2 className="font-display text-4xl md:text-5xl font-bold mb-4 text-balance">
            {t('landing.ctaTitle')}
          </h2>
          <p className="text-muted text-lg mb-10">{t('landing.ctaDesc')}</p>
          <CTAButton to="/pricing">{t('landing.viewPricing')}</CTAButton>
        </motion.div>
      </section>
    </>
  );
}

function CTAButton({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="relative inline-block group">
      <span className="absolute -inset-0.5 bg-primary/30 rounded blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      <span className="relative text-base px-6 py-3 rounded bg-primary text-white font-medium inline-flex items-center transition-colors group-hover:bg-primary-hover">
        {children}
      </span>
    </Link>
  );
}
